import { ISkillDemand } from 'core/hooks/Growth/transform/transformSkillMap';

import { TaskTypes } from 'context';

export type AttendanceType = {
  attendanceId: string;
  /** Backend student id — used to match join-click hints (spec 0013). */
  studentId?: string;
  studentName: string;
  checked?: boolean;
  /**
   * ТЗ 0026: «не отмечен» — отдельное состояние, а не «отсутствовал».
   * `checked` остаётся булевым для чекбокса, но подпись в строке должна их
   * различать: пустой журнал и журнал, где всем проставлено «не был», — это
   * совершенно разные ситуации.
   */
  isUnmarked?: boolean;
  /** 'auto' | 'manual' | '' — кто поставил текущее значение. */
  source?: string;
  /** Время отметки; для auto это время ПЕРВОГО входа. */
  markedAt?: string | null;
  markedByName?: string | null;
};

export type LessonAttachmentType = {
  id: string;
  title: string;
  link: string;
};

export enum AttachmentType {
  student = 'student',
  teacher = 'teacher',
}

export enum HomeworkGradesType {
  DONE = 'DONE',
  CONTAINS_ERRORS = 'CONTAINS_ERRORS',
  NOT_DONE = 'NOT_DONE',
}

export type HomeworksType = {
  studentId: string;
  studentName: string;
  gradeType?: HomeworkGradesType | null;
  mark?: number | null;
};

/** Навык, который открывается на занятии (ТЗ 0040, доводка). */
export type LessonSkill = {
  id: string;
  name: string;
  slug: string;
  category: string;
  iconUrl?: string | null;
  /** Одна строка про сам навык. Пустая у части справочника — тогда молчим. */
  description?: string;
  /**
   * Уровень, который даёт ЭТО занятие ПРЯМО СЕЙЧАС, 1..5.
   *
   * 🔴 Не уровень набора. Пока курс идёт, начисление ставит минимальный
   * уровень, а уровень набора приходит только по завершении курса — сервер
   * считает это за нас. Не «мой уровень»: своего мы на занятии не спрашиваем.
   */
  unlockLevel?: number | null;
  /** До какого уровня навык дорастёт по завершении всего курса. */
  courseLevel?: number | null;
  /** Востребованность из ночного снимка — в НАШЕЙ ленте вакансий. */
  demand?: ISkillDemand | null;
};

export type ProgramLessonType = {
  id: string;
  name: string;
  index: number;
  isEnded: boolean;
  isActive?: boolean;
  needToFinish?: boolean | null;
  /**
   * Что студент получает на этом занятии. Пусто — занятие навыков не даёт,
   * и экран о них молчит.
   *
   * 🔴 Сопоставление идёт по ID занятия, а не по его номеру: в системе три
   * разные нумерации, и две из них считаются по дате.
   */
  skills?: LessonSkill[];
  /**
   * Занятие прошло → навык уже начислен. Признак приходит С СЕРВЕРА тем же
   * условием, по которому начисляет ночная задача: считать его на клиенте по
   * дате значило бы обещать студенту не то, что придёт (у занятия есть ещё
   * ручная отметка «завершено»).
   */
  skillsGranted?: boolean;
};

export type LessonType = ProgramLessonType & {
  /** «Piątek, 2 października, 19:00–22:00» — местное время студента (ТЗ 0100). */
  time: string;
  /** ТЗ 0100: моменты начала/конца (ISO с офсетом) — окно входа, «сегодня/прошло». */
  startsAt?: string | null;
  endsAt?: string | null;
  date: string;
  finished: boolean;
  lmsDescription: string;
  comment: string;
  video?: string | null;
  taskTypes: string[];
  hasStudentMarks?: boolean | null;
  hasTeacherAttachment?: boolean | null;
  hasStudentAttachment?: boolean | null;
  whiteboardId?: string | null;
  homeworkAssignmentId?: string | null;
  /** rev.3: server gate — homework tab is available on every non-diploma
   * lesson, independent of a teacher-authored assignment. */
  homeworkAvailable?: boolean | null;
  /** spec 0013: the group has a configured join link (URL itself is never
   * exposed in queries — only via the lmsJoinLesson mutation). */
  hasJoinLink?: boolean | null;
  /** spec 0013: a temporary link override is active for today. */
  linkOverrideActive?: boolean | null;
};

export type StudentMarks = {
  mark?: number | null;
  hwMark?: HomeworkGradesType | null;
  date: string;
  taskType: TaskTypes;
};
